import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getSalesOrders,
  getSalesOrder,
  createDN,
  addDNItem,
  stockOut,
  getStockByBarcode,
  getDeliveryStatus,
  getDNItemsBySO,
  getDNItemsBySOBatch,
  getDNHeadersBySO,
  getDNItemsByDNID,
  recomputeDeliveryStatus,
  getSOBatches,
  getBatchItems,
  returnDNBarcode,
  cancelDN,
  getDNItemsDetailBySO,
  getSalesOrderTimeline,
  markDNDelivered,
} from "../../services/apiService";

import DnPrintTemplate from "./DnPrintTemplate";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./DeliveryNotes.css";

const toDateOnlyStr = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const getTodayDateInput = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (v) => {
  if (!v) return "-";

  const date = new Date(v);

  if (Number.isNaN(date.getTime())) {
    return toDateOnlyStr(v);
  }

  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getSODateValue = (so) => {
  return (
    so?.CreatedAt ||
    so?.createdat ||
    so?.CreatedDate ||
    so?.createddate ||
    so?.created_at ||
    so?.DateCreated ||
    so?.datecreated ||
    so?.SODate ||
    so?.sodate ||
    ""
  );
};

const matchesDateRange = (dateValue, from, to) => {
  const dateOnly = toDateOnlyStr(dateValue);

  if (!from && !to) return true;
  if (!dateOnly) return false;

  if (from && dateOnly < from) return false;
  if (to && dateOnly > to) return false;

  return true;
};

const sameDateOnly = (a, b) => toDateOnlyStr(a) === toDateOnlyStr(b);

const normalizeStatus = (value) => {
  return String(value ?? "").trim().toUpperCase();
};

const getUserRoleId = () => {
  let sessionUser = {};
  let localUser = {};

  try {
    sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    sessionUser = {};
  }

  try {
    localUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    localUser = {};
  }

  const user = Object.keys(sessionUser).length ? sessionUser : localUser;

  return Number(
    user.RoleID ??
    user.RoleId ??
    user.roleId ??
    user.roleID ??
    0
  );
};

export default function DeliveryNotes() {
  const navigate = useNavigate();
  const location = useLocation();

  const roleId = getUserRoleId();

  // Role 8  = View / Print only
  // Role 15 = Delivery monitoring only:
  //           View Timeline, View / Print, and Mark Delivered only.
  // Role 5  = Hide Mark Delivered button.
  const isPrintPreviewOnlyRole = roleId === 8;
  const isRole15DeliveryMonitor = roleId === 15;

  // Role 5 must NOT see/use Mark Delivered.
  // Role 15 can still View / Print and Mark Delivered only.
  const canMarkDeliveredDN = !isPrintPreviewOnlyRole && roleId !== 5;

  const isRestrictedDeliveryRole = isPrintPreviewOnlyRole || isRole15DeliveryMonitor;

  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSO, setSelectedSO] = useState(null);

  const [soItems, setSOItems] = useState([]);
  const [scanned, setScanned] = useState([]);
  const [barcode, setBarcode] = useState("");
  const [specialInstruction, setSpecialInstruction] = useState("");

  const [dbStatus, setDbStatus] = useState({
    status: "PENDING",
    completed: false,
    ordered: 0,
    delivered: 0,
    released: 0,
    cancelReturnCount: 0,
  });

  const [dnItems, setDnItems] = useState([]);
  const [dnHistory, setDnHistory] = useState([]);

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printDN, setPrintDN] = useState(null);
  const [printItems, setPrintItems] = useState([]);
  const [copyType, setCopyType] = useState("CUSTOMER'S COPY");
  const printRef = useRef(null);

  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");

  const [soPage, setSoPage] = useState(1);
  const [soPageSize, setSoPageSize] = useState(10);
  const [soSearch, setSoSearch] = useState("");
  const [soStatusFilter, setSoStatusFilter] = useState("ALL");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("ALL");
  const [lotApprovalFilter, setLotApprovalFilter] = useState("ALL");

  const [soDateFrom, setSoDateFrom] = useState("");
  const [soDateTo, setSoDateTo] = useState("");

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnDN, setReturnDN] = useState(null);
  const [returnDnItems, setReturnDnItems] = useState([]);
  const [returnBarcode, setReturnBarcode] = useState("");
  const [returnScanned, setReturnScanned] = useState([]);
  const [returnSaving, setReturnSaving] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelDNHeader, setCancelDNHeader] = useState(null);
  const [cancelDnItems, setCancelDnItems] = useState([]);
  const [cancelBarcode, setCancelBarcode] = useState("");
  const [cancelScanned, setCancelScanned] = useState([]);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [soTimeline, setSoTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);

  const [showMarkDeliveredModal, setShowMarkDeliveredModal] = useState(false);
  const [markDeliveredDN, setMarkDeliveredDN] = useState(null);
  const [markDeliveredDate, setMarkDeliveredDate] = useState(getTodayDateInput());
  const [markDeliveredSaving, setMarkDeliveredSaving] = useState(false);

  const soidFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("soid");
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.search]);

  const getSOStatusValue = (so) => {
    return normalizeStatus(so?.SOStatus || so?.sostatus || "PENDING");
  };

  const getDeliveryStatusValue = (so) => {
    return normalizeStatus(so?.DeliveryStatus || so?.deliverystatus || "PENDING");
  };

  const getLotApprovalStatusValue = (so) => {
    return normalizeStatus(
      so?.LotApprovalStatus ||
      so?.lotapprovalstatus ||
      so?.lotApprovalStatus ||
      "PENDING"
    );
  };

  const lotApprovalStatusLabel = (value) => {
    const key = normalizeStatus(value);

    const map = {
      PENDING: "Pending",
      APPROVED: "Approved",
      CANCELLED: "Cancelled",
      CANCEL: "Cancelled",
    };

    return map[key] || value || "-";
  };

  const selectedLotApprovalStatus = selectedSO
    ? getLotApprovalStatusValue(selectedSO)
    : "PENDING";

  const isLotApprovalBlocked =
    selectedSO && selectedLotApprovalStatus !== "APPROVED";

  const filteredSalesOrders = useMemo(() => {
    const keyword = soSearch.trim().toLowerCase();

    return (salesOrders || []).filter((so) => {
      const soNumber = so.sonumber || so.SONumber || "";
      const customer = so.customername || so.CustomerName || "";
      const shipTo = so.shipto || so.ShipTo || "";
      const orderType = so.ordertype || so.OrderType || "";
      const deliveryMode = so.deliverymode || so.DeliveryMode || "";
      const soStatus = getSOStatusValue(so);
      const deliveryStatus = getDeliveryStatusValue(so);
      const lotApprovalStatus = getLotApprovalStatusValue(so);
      const soDateValue = getSODateValue(so);

      const matchesSearch =
        !keyword ||
        `${soNumber} ${customer} ${shipTo} ${orderType} ${deliveryMode} ${soStatus} ${deliveryStatus} ${lotApprovalStatus}`
          .toLowerCase()
          .includes(keyword);

      const matchesSOStatus =
        soStatusFilter === "ALL" ||
        (soStatusFilter === "CANCELLED"
          ? soStatus === "CANCELLED" || soStatus === "CANCEL"
          : soStatus === soStatusFilter);

      const matchesDeliveryStatus =
        deliveryStatusFilter === "ALL" ||
        (deliveryStatusFilter === "CANCELLED"
          ? deliveryStatus === "CANCELLED" || deliveryStatus === "CANCEL"
          : deliveryStatus === deliveryStatusFilter);

      const matchesLotApproval =
        lotApprovalFilter === "ALL" || lotApprovalStatus === lotApprovalFilter;

      const matchesDate = matchesDateRange(soDateValue, soDateFrom, soDateTo);

      return (
        matchesSearch &&
        matchesSOStatus &&
        matchesDeliveryStatus &&
        matchesLotApproval &&
        matchesDate
      );
    });
  }, [
    salesOrders,
    soSearch,
    soStatusFilter,
    deliveryStatusFilter,
    lotApprovalFilter,
    soDateFrom,
    soDateTo,
  ]);

  const totalSORecords = filteredSalesOrders.length;

  const totalSOPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalSORecords / soPageSize));
  }, [totalSORecords, soPageSize]);

  const paginatedSalesOrders = useMemo(() => {
    const start = (soPage - 1) * soPageSize;
    const end = start + soPageSize;
    return filteredSalesOrders.slice(start, end);
  }, [filteredSalesOrders, soPage, soPageSize]);

  const soStartRecord = totalSORecords === 0 ? 0 : (soPage - 1) * soPageSize + 1;
  const soEndRecord = Math.min(soPage * soPageSize, totalSORecords);

  useEffect(() => {
    setSoPage(1);
  }, [
    soSearch,
    soStatusFilter,
    deliveryStatusFilter,
    lotApprovalFilter,
    soDateFrom,
    soDateTo,
    soPageSize,
  ]);

  useEffect(() => {
    setSoPage((prev) => Math.min(Math.max(prev, 1), totalSOPages));
  }, [totalSOPages]);

  const loadSO = useCallback(async () => {
    const data = await getSalesOrders();
    setSalesOrders(data || []);
  }, []);

  useEffect(() => {
    loadSO();
  }, [loadSO]);

  const isStaggered = (so) =>
    String(so?.DeliveryMode || so?.delivery_mode || "").toUpperCase() ===
    "STAGGERED";

  const isSOCancelled = useMemo(() => {
    const s = String(selectedSO?.SOStatus || selectedSO?.sostatus || "").toUpperCase();
    return s === "CANCEL" || s === "CANCELLED";
  }, [selectedSO]);

  const normalizedStatus = String(dbStatus?.status || "").toUpperCase();

  const isSOCompleted = normalizedStatus === "COMPLETE" || dbStatus.completed === true;
  const isSOReturned = normalizedStatus === "RETURNED";
  const isSODeliveryCancelled =
    normalizedStatus === "CANCELLED" || normalizedStatus === "CANCEL";

  const isDeliveryBlocked =
    isRestrictedDeliveryRole ||
    isSOCancelled ||
    isSOCompleted ||
    isSOReturned ||
    isSODeliveryCancelled ||
    isLotApprovalBlocked;

  const isReturnBlocked =
    isRestrictedDeliveryRole ||
    isSOCancelled ||
    isSOReturned ||
    isSODeliveryCancelled;

  const isCancelDNBlocked =
    isRestrictedDeliveryRole ||
    isSOCancelled ||
    isSOReturned ||
    isSODeliveryCancelled;

  const showHistoryItems =
    (dnItems.length > 0 && scanned.length === 0) ||
    isSOCompleted ||
    isSOReturned ||
    isSODeliveryCancelled;

  const buildRemainingFromSODetailLots = (so, deliveredRows) => {
    const deliveredMap = {};

    (deliveredRows || []).forEach((r) => {
      const itemId = r.ItemID ?? r.ItemId ?? r.itemID ?? r.itemId;
      const key = `${Number(itemId)}_${r.LotNumber}`;
      deliveredMap[key] = (deliveredMap[key] || 0) + Number(r.QtyDelivered || 0);
    });

    const merged = {};

    (so.items || []).forEach((item) => {
      (item.lots || []).forEach((lot) => {
        const key = `${Number(item.ItemId)}_${lot.LotNumber}`;

        if (!merged[key]) {
          merged[key] = {
            SODetailID: item.SODetailID,
            ItemID: item.ItemId,
            CatalogNumber: item.CatalogNo || item.CurrentCatalogNo,
            Description: item.ItemName || item.CurrentItemName,
            Packing: item.UnitOfMeasure,
            LotNumber: lot.LotNumber,
            ExpiryDate: toDateOnlyStr(lot.ExpiryDate),
            QtyRequired: 0,
            QtyRemaining: 0,
          };
        }

        merged[key].QtyRequired += Number(lot.Qty || 0);
      });
    });

    Object.keys(merged).forEach((k) => {
      const required = Number(merged[k].QtyRequired || 0);
      const delivered = Number(deliveredMap[k] || 0);
      merged[k].QtyRemaining = Math.max(required - delivered, 0);
    });

    return Object.values(merged);
  };

  const buildRemainingFromBatchPlan = (batchRows, deliveredRows) => {
    const deliveredMap = {};

    (deliveredRows || []).forEach((r) => {
      const itemId = r.ItemID ?? r.ItemId ?? r.itemID ?? r.itemId;
      const key = `${Number(itemId)}_${r.LotNumber}`;
      deliveredMap[key] = (deliveredMap[key] || 0) + Number(r.QtyDelivered || 0);
    });

    const merged = {};

    (batchRows || []).forEach((r) => {
      const key = `${Number(r.ItemId)}_${r.LotNumber}`;

      if (!merged[key]) {
        merged[key] = {
          SODetailID: r.SODetailID,
          ItemID: r.ItemId,
          CatalogNumber: r.CatalogNo,
          Description: r.ItemName,
          Packing: r.UnitOfMeasure || r.Packing || "",
          LotNumber: r.LotNumber,
          ExpiryDate: toDateOnlyStr(r.ExpiryDate),
          QtyRequired: 0,
          QtyRemaining: 0,
        };
      }

      merged[key].QtyRequired += Number(r.BatchQty ?? r.Qty ?? 0);
    });

    Object.keys(merged).forEach((k) => {
      const required = Number(merged[k].QtyRequired || 0);
      const delivered = Number(deliveredMap[k] || 0);
      merged[k].QtyRemaining = Math.max(required - delivered, 0);
    });

    return Object.values(merged);
  };

  const getTimelineTitle = (row) => {
    const action = String(row.ActionType || "").toUpperCase();
    const actor = row.ActionBy || "SYSTEM";

    if (action === "LOT_INPUT") {
      const count = Number(row.LotCount || 0);
      return `${count} lot${count > 1 ? "s" : ""} inputted by ${actor}`;
    }

    if (action === "LOT_UPDATE") {
      const count = Number(row.LotCount || 0);
      return `${count} lot${count > 1 ? "s" : ""} updated by ${actor}`;
    }

    if (action === "LOT_APPROVAL") {
      return `Lots approved by ${actor}`;
    }

    if (action === "DN_CREATED") {
      return `Delivery Note ${row.DNNumbers || ""} created by ${actor}`;
    }

    if (action === "DN_CANCELLED") {
      return `Delivery Note ${row.DNNumbers || ""} voided by ${actor}`;
    }

    if (action === "DN_ITEM_RELEASED") {
      return `${row.TotalReleasedQty || 0} item(s) released from stock by ${actor}`;
    }

    if (action === "DN_RELEASED") {
      return `Delivery Note ${row.DNNumbers || ""} released by ${actor}`;
    }

    if (action === "DN_DELIVERED") {
      return `Delivery Note ${row.DNNumbers || ""} marked as delivered by ${actor}`;
    }

    if (action === "DN_ITEM_DELIVERED") {
      return `${row.TotalDeliveredQty || 0} item(s) delivered by ${actor}`;
    }

    return `${row.ActionType || "Activity"} by ${actor}`;
  };

  const getTimelineBadgeClass = (type) => {
    const action = String(type || "").toUpperCase();

    if (action === "LOT_INPUT") return "timeline-badge lot-input";
    if (action === "LOT_UPDATE") return "timeline-badge lot-update";
    if (action === "LOT_APPROVAL") return "timeline-badge lot-approval";
    if (action === "DN_CREATED") return "timeline-badge dn-created";
    if (action === "DN_CANCELLED") return "timeline-badge dn-cancelled";
    if (action === "DN_ITEM_RELEASED") return "timeline-badge dn-released";
    if (action === "DN_RELEASED") return "timeline-badge dn-released";
    if (action === "DN_DELIVERED") return "timeline-badge dn-delivered";
    if (action === "DN_ITEM_DELIVERED") return "timeline-badge dn-delivered";

    return "timeline-badge";
  };

  const loadSOTimeline = useCallback(async (soid) => {
    if (!soid) {
      setSoTimeline([]);
      return;
    }

    try {
      setTimelineLoading(true);
      const rows = await getSalesOrderTimeline(soid);
      setSoTimeline(rows || []);
    } catch (err) {
      console.error("Load SO timeline error:", err);
      setSoTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  const openTimelineModal = async () => {
    if (!selectedSO) return;

    setShowTimelineModal(true);
    await loadSOTimeline(selectedSO.SOID || selectedSO.soid);
  };

  const closeTimelineModal = () => {
    setShowTimelineModal(false);
  };

  const renderTimelineContent = () => {
    if (timelineLoading) {
      return <div className="dn-timeline-empty">Loading timeline...</div>;
    }

    if (soTimeline.length === 0) {
      return <div className="dn-timeline-empty">No monitoring logs yet.</div>;
    }

    return (
      <div className="dn-timeline-list">
        {soTimeline.map((row) => (
          <div
            className="dn-timeline-row"
            key={`${row.ActionType}-${row.ActionGroupID || row.LogID}`}
          >
            <div className={getTimelineBadgeClass(row.ActionType)}>
              {String(row.ActionType || "").replace(/_/g, " ")}
            </div>

            <div className="dn-timeline-body">
              <div className="dn-timeline-main">
                <strong>{getTimelineTitle(row)}</strong>
                <span>{formatDateTime(row.ActionAt)}</span>
              </div>

              {row.LotNumbers ? (
                <div className="dn-timeline-sub">
                  Lots: {row.LotNumbers}
                </div>
              ) : null}

              {row.DNNumbers ? (
                <div className="dn-timeline-sub">
                  DN: {row.DNNumbers}
                </div>
              ) : null}

              {row.CatalogNumbers ? (
                <div className="dn-timeline-sub">
                  Catalog No: {row.CatalogNumbers}
                </div>
              ) : null}

              {row.TotalReleasedQty && String(row.ActionType).toUpperCase() === "DN_ITEM_RELEASED" ? (
                <div className="dn-timeline-sub">
                  Total Released Qty: {row.TotalReleasedQty}
                </div>
              ) : null}

              {row.TotalDeliveredQty && String(row.ActionType).toUpperCase() === "DN_ITEM_DELIVERED" ? (
                <div className="dn-timeline-sub">
                  Total Delivered Qty: {row.TotalDeliveredQty}
                </div>
              ) : null}

              {row.Remarks ? (
                <div className="dn-timeline-remarks">
                  {row.Remarks}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const refreshSOData = async (soid, forceBatchId = null) => {
    const so = await getSalesOrder(soid);
    setSelectedSO(so);
    const st = await getDeliveryStatus(soid);
    setDbStatus(st || { status: "PENDING", completed: false, ordered: 0, delivered: 0, released: 0 });

    const headers = await getDNHeadersBySO(soid);
    setDnHistory(headers || []);

    const deliveredAll = await getDNItemsDetailBySO(soid);
    setDnItems(deliveredAll || []);

    if (isStaggered(so)) {
      const b = await getSOBatches(soid);
      setBatches(b || []);

      const firstBatchId = b?.[0]?.BatchID ? String(b[0].BatchID) : "";
      const useBatchId = String(forceBatchId ?? selectedBatchId ?? firstBatchId ?? "");
      setSelectedBatchId(useBatchId);

      if (useBatchId) {
        const deliveredForBatch = await getDNItemsBySOBatch(soid, Number(useBatchId));
        const batchPlan = await getBatchItems(Number(useBatchId));
        setSOItems(buildRemainingFromBatchPlan(batchPlan || [], deliveredForBatch || []));
      } else {
        setSOItems([]);
      }
    } else {
      setBatches([]);
      setSelectedBatchId("");

      const deliveredRows = await getDNItemsBySO(soid);
      setSOItems(buildRemainingFromSODetailLots(so, deliveredRows || []));
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await loadSO();

      const currentSOID = selectedSO?.SOID || selectedSO?.soid || soidFromUrl;

      if (currentSOID) {
        await refreshSOData(currentSOID, selectedBatchId);
      }
    } catch (err) {
      console.error("Refresh Delivery Notes error:", err);
      alert(
        err?.response?.data?.error ||
        err?.message ||
        "Failed to refresh Delivery Notes."
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!soidFromUrl) {
      setSelectedSO(null);
      setSOItems([]);
      setDnItems([]);
      setDnHistory([]);
      setScanned([]);
      setBarcode("");
      setSpecialInstruction("");
      setPrintDN(null);
      setPrintItems([]);
      setShowPrintPreview(false);
      setDbStatus({ status: "PENDING", completed: false, ordered: 0, delivered: 0, released: 0 });
      setBatches([]);
      setSelectedBatchId("");
      setSoTimeline([]);
      setShowTimelineModal(false);

      setShowReturnModal(false);
      setReturnDN(null);
      setReturnDnItems([]);
      setReturnScanned([]);
      setReturnBarcode("");
      setReturnReason("");

      setShowCancelModal(false);
      setCancelDNHeader(null);
      setCancelDnItems([]);
      setCancelScanned([]);
      setCancelBarcode("");
      setCancelReason("");

      setShowMarkDeliveredModal(false);
      setMarkDeliveredDN(null);
      setMarkDeliveredDate(getTodayDateInput());
      setMarkDeliveredSaving(false);

      return;
    }

    if (selectedSO?.SOID === soidFromUrl) return;

    setScanned([]);
    setBarcode("");
    setSpecialInstruction("");
    setPrintDN(null);
    setPrintItems([]);
    setShowPrintPreview(false);
    setSoTimeline([]);
    setShowTimelineModal(false);

    setShowReturnModal(false);
    setReturnDN(null);
    setReturnDnItems([]);
    setReturnScanned([]);
    setReturnBarcode("");
    setReturnReason("");

    setShowCancelModal(false);
    setCancelDNHeader(null);
    setCancelDnItems([]);
    setCancelScanned([]);
    setCancelBarcode("");
    setCancelReason("");

    setShowMarkDeliveredModal(false);
    setMarkDeliveredDN(null);
    setMarkDeliveredDate(getTodayDateInput());
    setMarkDeliveredSaving(false);

    refreshSOData(soidFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soidFromUrl]);

  useEffect(() => {
    const run = async () => {
      if (!selectedSO) return;
      if (!isStaggered(selectedSO)) return;

      if (!selectedBatchId) {
        setSOItems([]);
        return;
      }

      const soid = selectedSO.SOID || selectedSO.soid;
      const deliveredForBatch = await getDNItemsBySOBatch(soid, Number(selectedBatchId));
      const batchPlan = await getBatchItems(Number(selectedBatchId));

      setSOItems(buildRemainingFromBatchPlan(batchPlan || [], deliveredForBatch || []));
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId]);

  const selectSO = (soid) => {
    navigate(`/delivery-notes?soid=${soid}`);
  };

  const backToSOList = () => {
    navigate("/delivery-notes");
  };

  const bannerLabel = () => {
    if (isSOCancelled) return "SO CANCELLED — Delivery is disabled";
    if (isSODeliveryCancelled) return "DN VOIDED — Barcode scanning disabled";
    if (isSOReturned) return "RETURNED — Barcode scanning disabled";
    if (normalizedStatus === "COMPLETE") return "DELIVERY COMPLETE — Print Only Mode";

    if (isLotApprovalBlocked) {
      return `LOT # ${selectedLotApprovalStatus} — DN creation is disabled`;
    }

    if (isRole15DeliveryMonitor) {
      return "DELIVERY MONITORING ONLY — View / Print and Mark Delivered are enabled";
    }

    if (isPrintPreviewOnlyRole) {
      return "PRINT PREVIEW ONLY — DN creation is disabled for this role";
    }

    if (normalizedStatus === "PARTIAL") return "PARTIAL DELIVERY — You can create another DN";
    return "PENDING — No actual delivery yet";
  };

  const bannerClass = () => {
    if (isSOCancelled) return "dn-banner-cancel";
    if (isSODeliveryCancelled) return "dn-banner-cancel";
    if (isSOReturned) return "dn-banner-partial";
    if (normalizedStatus === "COMPLETE") return "dn-banner-complete";
    if (isLotApprovalBlocked) return "dn-banner-cancel";
    if (isRole15DeliveryMonitor) return "dn-banner-partial";
    if (isPrintPreviewOnlyRole) return "dn-banner-partial";
    if (normalizedStatus === "PARTIAL") return "dn-banner-partial";
    return "dn-banner-pending";
  };

  const orderTypeLabel = (v) => {
    const map = {
      NORMAL: "Normal",
      DEMO: "Demo",
      FREE_GOODS: "Free Goods",
      BIOCARE_ACCOUNT: "Biocare Account",
      REPLACEMENT: "Replacement",
      AS_TESTING: "AS Testing",
      TSD_TESTING: "TSD Testing",
      ADVANCE_DELIVERY: "Advance Delivery",
      PMS: "PMS",
      MARKETING_EVENT: "Marketing Event",
    };

    return map[v] || v || "-";
  };

  const deliveryModeLabel = (v) => {
    const map = {
      SINGLE: "Single",
      STAGGERED: "Staggered",
    };

    return map[v] || v || "-";
  };

  const deliveryStatusLabel = (v) => {
    const map = {
      PENDING: "Pending",
      PARTIAL: "Partial",
      COMPLETE: "Complete",
      RETURNED: "Returned",
      CANCELLED: "Voided",
      CANCEL: "Voided",
      VOIDED: "Voided",
    };

    return map[String(v || "").toUpperCase()] || v || "-";
  };

  const getItemStatusLabel = (it) => {
    const itemStatus = normalizeStatus(it?.ItemStatus || it?.itemstatus);
    const action = normalizeStatus(it?.ReturnAction || it?.returnaction);
    const dnStatus = normalizeStatus(it?.DNStatus || it?.dnstatus);

    if (
      itemStatus === "CANCELLED" ||
      itemStatus === "CANCEL" ||
      itemStatus === "VOIDED" ||
      action === "STOCK_CANCEL_RETURN" ||
      dnStatus === "CANCEL" ||
      dnStatus === "CANCELLED" ||
      dnStatus === "VOIDED"
    ) {
      return "VOIDED";
    }

    if (
      itemStatus === "RETURNED" ||
      action === "STOCK_RETURN" ||
      Number(it?.IsReturned) === 1
    ) {
      return "RETURNED";
    }

    if (itemStatus === "DELIVERED") {
      return "DELIVERED";
    }

    return "RELEASED";
  };

  const getItemReason = (it) => {
    const cancelReason =
      it?.CancelReason ||
      it?.cancelReason ||
      it?.cancelreason ||
      "";

    const returnReason =
      it?.ReturnReason ||
      it?.returnReason ||
      it?.returnreason ||
      "";

    const status = getItemStatusLabel(it);

    if (status === "VOIDED") return cancelReason || returnReason || "-";
    if (status === "RETURNED") return returnReason || cancelReason || "-";

    return "-";
  };

  const getUnitMeasure = (it) => {
    return (
      it?.Packing ||
      it?.UnitOfMeasure ||
      it?.UnitMeasure ||
      it?.unit_of_measure ||
      it?.unitofmeasure ||
      it?.uom ||
      "-"
    );
  };

  const getDnCancelReason = (dn) => {
    return (
      dn?.CancelReason ||
      dn?.cancelReason ||
      dn?.cancelreason ||
      ""
    );
  };

  const getDNReceivedDate = (dn) => {
    return (
      toDateOnlyStr(
        dn?.DeliveredDate ||
        dn?.DeliveredAt ||
        dn?.deliveredDate ||
        dn?.deliveredat ||
        dn?.DNDeliveredDate ||
        dn?.DNDeliveredAt
      ) || "-"
    );
  };

  const getItemReceivedDate = (it) => {
    return (
      toDateOnlyStr(
        it?.ActualDeliveredDate ||
        it?.ActualDeliveredAt ||
        it?.actualDeliveredDate ||
        it?.actualdeliveredat ||
        it?.DNDeliveredDate ||
        it?.DNDeliveredAt
      ) || "-"
    );
  };

  const handleBarcode = async (e) => {
    if (e.key !== "Enter") return;

    if (isRestrictedDeliveryRole) {
      alert(
        isRole15DeliveryMonitor
          ? "This role can only View / Print and Mark Delivered."
          : "This role is Print Preview only."
      );
      setBarcode("");
      return;
    }

    if (isLotApprovalBlocked) {
      alert(`Cannot scan. Lot # approval status is ${selectedLotApprovalStatus}.`);
      setBarcode("");
      return;
    }

    if (isDeliveryBlocked) return;
    if (!barcode.trim()) return;

    if (isStaggered(selectedSO) && !selectedBatchId) {
      alert("⚠ Please select a Batch first.");
      setBarcode("");
      return;
    }

    const clean = barcode.trim();

    const alreadyScanned = scanned.some((x) => x.Barcode === clean);
    if (alreadyScanned) {
      alert("⚠ This barcode is already scanned.");
      setBarcode("");
      return;
    }

    try {
      const stock = await getStockByBarcode(clean);

      const stockItemID = stock?.item?.ItemID;
      const stockLot = stock?.item?.LotNumber;
      const stockExpiry = stock?.item?.ExpiryDate || "";
      const stockExpiryOnly = toDateOnlyStr(stockExpiry);
      const whID = stock?.box?.WarehouseID || null;

      const matchIndex = soItems.findIndex(
        (l) =>
          Number(l.ItemID) === Number(stockItemID) &&
          String(l.LotNumber || "") === String(stockLot || "") &&
          (stockExpiryOnly ? sameDateOnly(l.ExpiryDate, stockExpiryOnly) : true)
      );

      if (matchIndex === -1) {
        alert("⚠ LOT NOT FOUND IN THIS CURRENT PLAN (SO / BATCH).");
        setBarcode("");
        return;
      }

      const matchedLot = soItems[matchIndex];

      if (matchedLot.QtyRemaining <= 0) {
        alert("⚠ No more qty remaining for this lot.");
        setBarcode("");
        return;
      }

      setScanned((prev) => [
        ...prev,
        {
          ...matchedLot,
          Barcode: clean,
          QtyDelivered: 1,
          WarehouseID: whID,
          ExpiryDate: toDateOnlyStr(matchedLot.ExpiryDate),
        },
      ]);

      const updated = [...soItems];
      updated[matchIndex] = {
        ...matchedLot,
        QtyRemaining: matchedLot.QtyRemaining - 1,
      };

      setSOItems(updated);
      setBarcode("");
    } catch (err) {
      alert("⚠ BARCODE NOT FOUND.");
      setBarcode("");
    }
  };

  const cancelDelivery = () => backToSOList();

  const confirmDelivery = async () => {
    if (!selectedSO) return alert("Please select a Sales Order first.");

    if (isRestrictedDeliveryRole) {
      return alert(
        isRole15DeliveryMonitor
          ? "This role can only View / Print and Mark Delivered. DN creation is disabled."
          : "This role is Print Preview only. DN creation is disabled."
      );
    }

    if (selectedLotApprovalStatus !== "APPROVED") {
      return alert(
        `Cannot proceed to DN. Lot # approval status is ${selectedLotApprovalStatus}.`
      );
    }

    if (isSOCancelled) return alert("SO is CANCELLED. Delivery is not allowed.");
    if (isSOCompleted) return alert("SO already COMPLETE.");
    if (isSOReturned) return alert("SO status is RETURNED. Delivery is not allowed.");
    if (isSODeliveryCancelled) return alert("SO status is CANCELLED. Delivery is not allowed.");

    if (scanned.length === 0) return alert("No scanned items.");

    if (isStaggered(selectedSO) && !selectedBatchId) {
      return alert("⚠ Please select a Batch first.");
    }

    try {
      const soid = selectedSO.SOID || selectedSO.soid;

      const dnHeader = await createDN({
        SOID: soid,
        BatchID: isStaggered(selectedSO) ? Number(selectedBatchId) : null,
        Remarks: "",
        SpecialInstruction: specialInstruction,
      });

      const DNID = dnHeader.DNID;

      for (const item of scanned) {
        await addDNItem({
          DNID,
          SODetailID: item.SODetailID,
          ItemID: item.ItemID,
          WarehouseID: item.WarehouseID,
          CatalogNumber: item.CatalogNumber,
          Description: item.Description,
          Packing: item.Packing,
          LotNumber: item.LotNumber,
          ExpiryDate: toDateOnlyStr(item.ExpiryDate),
          Barcode: item.Barcode,
          QtyDelivered: item.QtyDelivered,
        });
      }

      for (const item of scanned) {
        await stockOut({ barcode: item.Barcode });
      }

      await recomputeDeliveryStatus(soid);
      await refreshSOData(soid, selectedBatchId);

      const itemsForThisDN = await getDNItemsByDNID(DNID);
      setPrintDN(dnHeader);
      setPrintItems(itemsForThisDN || []);
      setCopyType("CUSTOMER'S COPY");
      setShowPrintPreview(true);

      setScanned([]);
      setBarcode("");
      setSpecialInstruction("");
    } catch (err) {
      console.error("confirmDelivery ERROR:", err);
      alert(
        err?.response?.data?.error ||
        err?.message ||
        "Failed to release items / create Delivery Note."
      );
    }
  };

  const downloadDNCopiesPDF = async () => {
    if (!printRef.current) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const copyTypes = [
      "CUSTOMER'S COPY",
      "RECEIVING COPY",
      "WAREHOUSE COPY",
      "SALES COPY",
    ];

    let pdfPageCount = 0;

    try {
      for (const currentCopyType of copyTypes) {
        setCopyType(currentCopyType);

        // Wait until React has rendered the new copy label.
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });

        // Wait for web fonts before taking the screenshot, when supported.
        if (document.fonts?.ready) {
          await document.fonts.ready;
        }

        const printPages = Array.from(
          printRef.current.querySelectorAll(".dn-print-page")
        );

        if (!printPages.length) {
          throw new Error("No Delivery Note print pages were found.");
        }

        for (const printPage of printPages) {
          const canvas = await html2canvas(printPage, {
            scale: 3,
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#ffffff",
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: Math.max(1200, window.innerWidth),
            onclone: (_document, clonedPage) => {
              // html2canvas otherwise clips the sheet to scrollable modal ancestors.
              let ancestor = clonedPage.parentElement;
              while (ancestor) {
                ancestor.style.setProperty("overflow", "visible", "important");
                ancestor.style.setProperty("max-height", "none", "important");
                ancestor = ancestor.parentElement;
              }
            },
          });

          const imgData = canvas.toDataURL("image/png");

          // Fit exactly one captured .dn-print-page on one A4 PDF page.
          const canvasRatio = canvas.width / canvas.height;
          const a4Ratio = pageWidth / pageHeight;

          let imgWidth;
          let imgHeight;

          if (canvasRatio > a4Ratio) {
            imgWidth = pageWidth;
            imgHeight = pageWidth / canvasRatio;
          } else {
            imgHeight = pageHeight;
            imgWidth = pageHeight * canvasRatio;
          }

          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;

          if (pdfPageCount > 0) {
            doc.addPage("a4", "portrait");
          }

          doc.addImage(
            imgData,
            "PNG",
            x,
            y,
            imgWidth,
            imgHeight,
            undefined,
            "FAST"
          );

          pdfPageCount += 1;
        }
      }

      const dnNo = printDN?.DNNumber || "DN";
      doc.save(`${dnNo}_COPIES.pdf`);
    } catch (error) {
      console.error("downloadDNCopiesPDF ERROR:", error);
      alert(error?.message || "Failed to generate the Delivery Note PDF.");
    } finally {
      setCopyType("CUSTOMER'S COPY");
    }
  };

  const countReturnScans = (barcodeValue) =>
    returnScanned.reduce((acc, x) => acc + (x.Barcode === barcodeValue ? 1 : 0), 0);

  const handleReturnBarcode = (e) => {
    if (e.key !== "Enter") return;

    const clean = returnBarcode.trim();
    if (!clean) return;

    const dnItem = (returnDnItems || []).find((x) => String(x.Barcode) === clean);

    if (!dnItem) {
      alert("⚠ Barcode not found in this DN.");
      setReturnBarcode("");
      return;
    }

    const maxQty = Number(dnItem.QtyDelivered || 1);
    const already = countReturnScans(clean);

    if (already >= maxQty) {
      alert("⚠ Already scanned max returnable qty for this barcode.");
      setReturnBarcode("");
      return;
    }

    setReturnScanned((prev) => [
      ...prev,
      {
        Barcode: clean,
        ItemID: dnItem.ItemID,
        WarehouseID: dnItem.WarehouseID,
        LotNumber: dnItem.LotNumber,
        ExpiryDate: toDateOnlyStr(dnItem.ExpiryDate),
        Description: dnItem.Description,
        Packing: dnItem.Packing || dnItem.UnitOfMeasure || dnItem.UnitMeasure || "",
        Qty: 1,
      },
    ]);

    setReturnBarcode("");
  };

  const confirmReturnToStock = async () => {
    if (!returnDN) return;
    if (returnScanned.length === 0) return alert("No scanned items to return.");
    if (!selectedSO) return;

    if (!returnReason.trim()) {
      return alert("⚠ Please enter a Return Reason first.");
    }

    setReturnSaving(true);

    try {
      for (const x of returnScanned) {
        await returnDNBarcode(returnDN.DNID, x.Barcode, returnReason);
      }

      const soid = selectedSO.SOID || selectedSO.soid;

      await recomputeDeliveryStatus(soid);
      alert("Returned to stock!");

      setShowReturnModal(false);
      setReturnDN(null);
      setReturnDnItems([]);
      setReturnScanned([]);
      setReturnBarcode("");
      setReturnReason("");

      await refreshSOData(soid, selectedBatchId);
    } catch (err) {
      console.error(err);
      alert("Failed to return (maybe already returned).");
    } finally {
      setReturnSaving(false);
    }
  };

  const countCancelScans = (barcodeValue) =>
    cancelScanned.reduce((acc, x) => acc + (x.Barcode === barcodeValue ? 1 : 0), 0);

  const handleCancelBarcode = (e) => {
    if (e.key !== "Enter") return;

    const clean = cancelBarcode.trim();
    if (!clean) return;

    const dnItem = (cancelDnItems || []).find((x) => String(x.Barcode) === clean);

    if (!dnItem) {
      alert("⚠ Barcode not found in this DN.");
      setCancelBarcode("");
      return;
    }

    const maxQty = Number(dnItem.QtyDelivered || 1);
    const already = countCancelScans(clean);

    if (already >= maxQty) {
      alert("⚠ Already scanned max cancelable qty for this barcode.");
      setCancelBarcode("");
      return;
    }

    setCancelScanned((prev) => [
      ...prev,
      {
        Barcode: clean,
        LotNumber: dnItem.LotNumber,
        ExpiryDate: toDateOnlyStr(dnItem.ExpiryDate),
        Description: dnItem.Description,
        Packing: dnItem.Packing || dnItem.UnitOfMeasure || dnItem.UnitMeasure || "",
        Qty: 1,
      },
    ]);

    setCancelBarcode("");
  };

  const confirmCancelDN = async () => {
    if (!cancelDNHeader) return;
    if (!selectedSO) return;

    const cleanReason = cancelReason.trim();

    if (!cleanReason) {
      return alert("⚠ Please enter a Cancel Reason first.");
    }

    if (cancelScanned.length === 0) {
      return alert("⚠ No scanned items for cancel.");
    }

    setCancelSaving(true);

    try {
      const barcodes = cancelScanned.map((x) => x.Barcode);
      // const soid = selectedSO.SOID || selectedSO.soid;

      await cancelDN(cancelDNHeader.DNID, cleanReason, barcodes);
      setSelectedSO((prev) =>
        prev
          ? {
            ...prev,
            SOStatus: "CANCELLED",
            sostatus: "CANCELLED",
            DeliveryStatus: "CANCELLED",
            deliverystatus: "CANCELLED",
            LotApprovalStatus: "CANCELLED",
            lotapprovalstatus: "CANCELLED",
          }
          : prev
      );

      setDbStatus((prev) => ({
        ...prev,
        status: "CANCELLED",
        completed: false,
      }));

      setDnHistory((prev) =>
        (prev || []).map((dn) =>
          Number(dn.DNID) === Number(cancelDNHeader.DNID)
            ? {
              ...dn,
              DNStatus: "CANCEL",
              dnstatus: "CANCEL",
              CancelReason: cleanReason,
              cancelReason: cleanReason,
              Remarks: dn.Remarks
                ? `${dn.Remarks} | Cancelled: ${cleanReason}`
                : `Cancelled: ${cleanReason}`,
            }
            : dn
        )
      );

      setDnItems((prev) => {
        const otherItems = (prev || []).filter(
          (item) => Number(item.DNID) !== Number(cancelDNHeader.DNID)
        );

        const cancelledItems = (cancelDnItems || []).map((item) => ({
          ...item,
          DNID: cancelDNHeader.DNID,
          DNNumber: cancelDNHeader.DNNumber,
          DNStatus: "CANCEL",
          dnstatus: "CANCEL",
          ItemStatus: "CANCELLED",
          ReturnAction: "STOCK_CANCEL_RETURN",
          IsReturned: 1,
          CancelReason: cleanReason,
          cancelReason: cleanReason,
          ReturnReason: cleanReason,
          returnReason: cleanReason,
        }));

        return [...cancelledItems, ...otherItems];
      });

      setSOItems([]);
      setScanned([]);
      setBarcode("");
      setSpecialInstruction("");

      await loadSO();
      alert("✅ DN Voided, stock returned, and SO cancelled.");

      setShowCancelModal(false);
      setCancelDNHeader(null);
      setCancelDnItems([]);
      setCancelBarcode("");
      setCancelScanned([]);
      setCancelReason("");
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.error ||
        err?.message ||
        "❌ Failed to cancel DN. Check missing/unexpected scans."
      );
    } finally {
      setCancelSaving(false);
    }
  };

  const openMarkDeliveredModal = (dn) => {
    if (!canMarkDeliveredDN) return;

    setMarkDeliveredDN(dn);
    setMarkDeliveredDate(
      toDateOnlyStr(
        dn?.DeliveredDate ||
        dn?.DeliveredAt ||
        dn?.deliveredDate ||
        dn?.deliveredat
      ) || getTodayDateInput()
    );
    setShowMarkDeliveredModal(true);
  };

  const confirmMarkDelivered = async () => {
    if (!canMarkDeliveredDN) return;
    if (!markDeliveredDN) return;

    if (!markDeliveredDate) {
      return alert("Please select the received date.");
    }

    setMarkDeliveredSaving(true);

    try {
      const soid = selectedSO.SOID || selectedSO.soid;

      await markDNDelivered(markDeliveredDN.DNID, markDeliveredDate);

      await refreshSOData(soid, selectedBatchId);
      await loadSO();

      alert("DN marked as delivered.");

      setShowMarkDeliveredModal(false);
      setMarkDeliveredDN(null);
      setMarkDeliveredDate(getTodayDateInput());
    } catch (err) {
      console.error("MARK DELIVERED ERROR:", err);

      alert(
        err?.response?.data?.error ||
        err?.message ||
        "Failed to mark DN as delivered."
      );
    } finally {
      setMarkDeliveredSaving(false);
    }
  };

  return (
    <div className="dn-container">
      <div className="dn-page-header">
        <div>
          <div className="dn-page-kicker">Logistics Workspace</div>
          <h2>Delivery Notes</h2>
          <p className="dn-page-subtitle">
            Manage delivery scanning, delivery monitoring, and print-ready delivery notes.
          </p>
        </div>

        <div className="dn-header-actions">
          <button
            type="button"
            className="confirm-btn dn-refresh-btn"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "↻ Refresh"}
          </button>

          {selectedSO && (
            <button className="cancel-btn dn-back-btn" onClick={backToSOList}>
              ← Back to SO List
            </button>
          )}
        </div>
      </div>

      {!selectedSO && (
        <div className="premium-card so-list-card">
          <div className="card-header">
            <div>
              <h3>Select Sales Order</h3>
              <p>Choose a sales order to create and manage delivery notes.</p>
            </div>

            <div className="card-badge">{totalSORecords} Orders</div>
          </div>

          <div className="dn-so-filter-bar">
            <div className="dn-so-search-box">
              <input
                type="text"
                placeholder="Search SO number, customer, ship to, order type..."
                value={soSearch}
                onChange={(e) => setSoSearch(e.target.value)}
              />
            </div>

            <div className="dn-so-filter-group">
              <label>Date From</label>
              <input
                type="date"
                value={soDateFrom}
                max={soDateTo || undefined}
                onChange={(e) => setSoDateFrom(e.target.value)}
                className="dn-filter-bordered"
              />
            </div>

            <div className="dn-so-filter-group">
              <label>Date To</label>
              <input
                type="date"
                value={soDateTo}
                min={soDateFrom || undefined}
                onChange={(e) => setSoDateTo(e.target.value)}
                className="dn-filter-bordered"
              />
            </div>

            <div className="dn-so-filter-group">
              <label>Lot Approval</label>
              <select
                value={lotApprovalFilter}
                onChange={(e) => setLotApprovalFilter(e.target.value)}
                className="dn-filter-bordered"
              >
                <option value="ALL">All Lot Approval</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            <div className="dn-so-filter-group">
              <label>Delivery Status</label>
              <select
                value={deliveryStatusFilter}
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <option value="ALL">All Delivery</option>
                <option value="PENDING">Pending</option>
                <option value="PARTIAL">Partial</option>
                <option value="COMPLETE">Complete</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="RETURNED">Returned</option>
              </select>
            </div>

            <button
              type="button"
              className="dn-so-clear-filter-btn"
              onClick={() => {
                setSoSearch("");
                setSoStatusFilter("ALL");
                setDeliveryStatusFilter("ALL");
                setLotApprovalFilter("ALL");
                setSoDateFrom("");
                setSoDateTo("");
              }}
            >
              Clear
            </button>
          </div>

          <div className="table-shell">
            <table className="premium-table so-list">
              <thead>
                <tr>
                  <th>SO #</th>
                  <th>Customer</th>
                  <th>Date Created</th>
                  <th>Order Type</th>
                  <th>Delivery Mode</th>
                  <th>Lot Approval</th>
                  <th>Delivery Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedSalesOrders.map((so) => {
                  const soid = so.soid || so.SOID;
                  const deliveryStatus = so.deliverystatus || so.DeliveryStatus || "PENDING";
                  const lotApprovalStatus =
                    so.lotapprovalstatus ||
                    so.LotApprovalStatus ||
                    so.lotApprovalStatus ||
                    "PENDING";

                  return (
                    <tr key={soid}>
                      <td>
                        <div className="cell-main">{so.sonumber || so.SONumber}</div>
                        <div className="cell-sub">Sales Order</div>
                      </td>

                      <td>{so.customername || so.CustomerName || "-"}</td>

                      <td>{formatDateTime(getSODateValue(so))}</td>

                      <td>{orderTypeLabel(so.ordertype || so.OrderType)}</td>

                      <td>{deliveryModeLabel(so.deliverymode || so.DeliveryMode)}</td>

                      <td>
                        <span
                          className={`status-chip status-${String(lotApprovalStatus || "")
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {lotApprovalStatusLabel(lotApprovalStatus)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`status-chip status-${String(deliveryStatus || "")
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {deliveryStatusLabel(deliveryStatus)}
                        </span>
                      </td>

                      <td>
                        <button className="select-btn" onClick={() => selectSO(soid)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {paginatedSalesOrders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty-state-cell">
                      No sales orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="dn-pagination">
            <div className="dn-pagination-info">
              Showing {soStartRecord} - {soEndRecord} of {totalSORecords} orders
            </div>

            <div className="dn-pagination-controls">
              <select
                className="dn-page-size"
                value={soPageSize}
                onChange={(e) => {
                  setSoPageSize(Number(e.target.value));
                  setSoPage(1);
                }}
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={15}>15 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>

              <button
                className="dn-page-btn"
                disabled={soPage <= 1}
                onClick={() => setSoPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>

              <span className="dn-page-number">
                Page {soPage} of {totalSOPages}
              </span>

              <button
                className="dn-page-btn"
                disabled={soPage >= totalSOPages}
                onClick={() => setSoPage((prev) => Math.min(prev + 1, totalSOPages))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSO && (
        <>
          <div className="dn-overview-grid">
            <div className="overview-card">
              <span className="overview-label">SO Number</span>
              <strong>{selectedSO.SONumber || selectedSO.sonumber || "-"}</strong>
            </div>

            <div className="overview-card">
              <span className="overview-label">Customer</span>
              <strong>{selectedSO.CustomerName || selectedSO.customername || "-"}</strong>
            </div>

            <div className="overview-card">
              <span className="overview-label">Delivery Mode</span>
              <strong>
                {deliveryModeLabel(selectedSO.DeliveryMode || selectedSO.deliverymode)}
              </strong>
            </div>

            <div className="overview-card">
              <span className="overview-label">Status</span>
              <strong>{deliveryStatusLabel(dbStatus.status)}</strong>
            </div>

            <div className="overview-card">
              <span className="overview-label">Lot Approval</span>
              <strong>{lotApprovalStatusLabel(selectedLotApprovalStatus)}</strong>
            </div>
          </div>

          <div className="premium-card dn-timeline-launch-card">
            <div className="dn-timeline-launch-left">
              <div className="dn-timeline-launch-icon">⏱</div>

              <div>
                <div className="dn-timeline-kicker">Monitoring</div>
                <h3>Sales Order Timeline</h3>
                <p>
                  View lot input, lot approval, DN creation, release, delivery,
                  and voiding activity.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="confirm-btn dn-timeline-open-btn"
              onClick={openTimelineModal}
            >
              View Timeline
            </button>
          </div>

          <div className="dn-body">
            <div className="dn-left premium-card">
              <div className="card-header">
                <div>
                  <h3>SO Items Remaining</h3>
                  <p>These are the lot-level items still available for delivery scanning.</p>
                </div>

                <div className="card-badge">{soItems.length} Lines</div>
              </div>

              {isStaggered(selectedSO) && (
                <div className="form-block">
                  <label className="premium-label">Batch</label>

                  <select
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                    className="premium-input"
                    disabled={
                      isSOCancelled ||
                      isRestrictedDeliveryRole ||
                      isLotApprovalBlocked
                    }
                  >
                    <option value="">-- Select Batch --</option>

                    {batches.map((b) => (
                      <option key={b.BatchID} value={String(b.BatchID)}>
                        Batch #{b.BatchNo} ({b.PlannedMonth || "no month"})
                      </option>
                    ))}
                  </select>

                  {!selectedBatchId && (
                    <div className="inline-note">
                      No batch selected. Create/select a batch in Logistics first.
                    </div>
                  )}
                </div>
              )}

              <div className="table-shell">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Catalog</th>
                      <th>Description</th>
                      <th>Lot</th>
                      <th>Expiry</th>
                      <th>Unit Measure</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>

                  <tbody>
                    {soItems.map((it, idx) => (
                      <tr key={idx} className={it.QtyRemaining === 0 ? "done-row" : ""}>
                        <td>{it.CatalogNumber}</td>
                        <td>{it.Description}</td>
                        <td>{it.LotNumber}</td>
                        <td>{toDateOnlyStr(it.ExpiryDate)}</td>
                        <td>{getUnitMeasure(it)}</td>
                        <td>
                          <span
                            className={
                              it.QtyRemaining === 0 ? "qty-pill qty-zero" : "qty-pill"
                            }
                          >
                            {it.QtyRemaining}
                          </span>
                        </td>
                      </tr>
                    ))}

                    {soItems.length === 0 && (
                      <tr>
                        <td colSpan={6} className="empty-state-cell">
                          No remaining items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dn-right premium-card">
              <div className={`dn-complete-banner ${bannerClass()}`}>
                <strong>{bannerLabel()}</strong>
                <br />
                <small>
                  <strong>Order Type:</strong> {selectedSO.OrderType || "-"} | Ordered:{" "}
                  {dbStatus.ordered} | Released: {dbStatus.released || 0} | Delivered: {dbStatus.delivered}
                </small>
              </div>

              {dnHistory.length > 0 && (
                <div className="history-card">
                  <div className="card-header compact">
                    <div>
                      <h3>DN History</h3>
                      <p>View delivery notes, print copies, and mark released DNs as delivered.</p>
                    </div>

                    <div className="card-badge">{dnHistory.length} DN</div>
                  </div>

                  <div className="table-shell">
                    <table className="premium-table">
                      <thead>
                        <tr>
                          <th>DN #</th>
                          <th>DN Date</th>
                          <th>Received Date</th>
                          <th>Status</th>
                          <th>Reason</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {dnHistory.map((dn) => {
                          const dnStatus = normalizeStatus(
                            dn.DNStatus || dn.dnstatus || "ACTIVE"
                          );
                          const dnProcessStatus = normalizeStatus(
                            dn.DNProcessStatus || dn.dnprocessstatus
                          );

                          const isCancelledDN =
                            dnStatus === "CANCEL" || dnStatus === "CANCELLED";
                          const cancelReasonText = getDnCancelReason(dn);

                          return (
                            <tr key={dn.DNID}>
                              <td>{dn.DNNumber}</td>
                              <td>{toDateOnlyStr(dn.DNDate)}</td>
                              <td>{getDNReceivedDate(dn)}</td>

                              <td>
                                {isCancelledDN ? (
                                  <span className="returned-badge">VOIDED</span>
                                ) : dnProcessStatus === "DELIVERED" ? (
                                  <span className="delivered-badge">DELIVERED</span>
                                ) : (
                                  <span className="released-badge">RELEASED</span>
                                )}
                              </td>

                              <td>{cancelReasonText || "-"}</td>

                              <td>
                                <div className="action-group">
                                  <button
                                    className="print-btn"
                                    onClick={async () => {
                                      const items = await getDNItemsByDNID(dn.DNID);
                                      setPrintDN(dn);
                                      setPrintItems(items || []);
                                      setCopyType("CUSTOMER'S COPY");
                                      setShowPrintPreview(true);
                                    }}
                                  >
                                    View / Print
                                  </button>

                                  {canMarkDeliveredDN && !isCancelledDN && (
                                    <button
                                      className="confirm-btn"
                                      disabled={dnProcessStatus === "DELIVERED"}
                                      onClick={() => openMarkDeliveredModal(dn)}
                                    >
                                      {dnProcessStatus === "DELIVERED"
                                        ? "Delivered"
                                        : "Mark Delivered"}
                                    </button>
                                  )}

                                  {!isRestrictedDeliveryRole && (
                                    <>
                                      <button
                                        className="confirm-btn"
                                        onClick={() =>
                                          navigate(`/delivery-notes/${dn.DNID}/print-edit`, {
                                            state: { SOID: selectedSO.SOID || selectedSO.soid },
                                          })
                                        }
                                      >
                                        Edit / Custom Print
                                      </button>

                                      <button
                                        className="cancel-btn"
                                        disabled={isCancelDNBlocked || cancelSaving || isCancelledDN}
                                        onClick={async () => {
                                          const ok = window.confirm(
                                            `Cancel DN ${dn.DNNumber}? (Requires scanning the items back)`
                                          );

                                          if (!ok) return;

                                          try {
                                            const items = await getDNItemsByDNID(dn.DNID);

                                            setCancelDNHeader(dn);
                                            setCancelDnItems(items || []);
                                            setCancelScanned([]);
                                            setCancelBarcode("");
                                            setCancelReason("");
                                            setShowCancelModal(true);
                                          } catch (err) {
                                            console.error("[CANCEL] getDNItemsByDNID failed", err);
                                            alert("Cancel failed. Check Console/Network.");
                                          }
                                        }}
                                      >
                                        Cancel
                                      </button>

                                      <button
                                        className="confirm-btn secondary"
                                        disabled={isReturnBlocked || isCancelledDN}
                                        onClick={async () => {
                                          try {
                                            const items = await getDNItemsByDNID(dn.DNID);

                                            setReturnDN(dn);
                                            setReturnDnItems(items || []);
                                            setReturnScanned([]);
                                            setReturnBarcode("");
                                            setReturnReason("");
                                            setShowReturnModal(true);
                                          } catch (err) {
                                            console.error("[RETURN] getDNItemsByDNID failed", err);
                                            alert("Return failed. Check Console/Network.");
                                          }
                                        }}
                                      >
                                        Return
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!isRestrictedDeliveryRole && (
                <div className="scan-panel">
                  <div className="card-header compact">
                    <div>
                      <h3>Scan Release / Stock Out</h3>
                      <p>Scan item barcodes to release/stock-out items for the current delivery note.</p>
                    </div>
                  </div>

                  <div className="scan-row single-col">
                    <div>
                      <label className="premium-label">Barcode</label>
                      <input
                        className="premium-input"
                        disabled={isDeliveryBlocked}
                        placeholder={
                          isSOCancelled
                            ? "SO CANCELLED"
                            : isSODeliveryCancelled
                              ? "DN VOIDED"
                              : isSOReturned
                                ? "RETURNED"
                                : isSOCompleted
                                  ? "DELIVERY COMPLETE"
                                  : isLotApprovalBlocked
                                    ? `LOT # ${selectedLotApprovalStatus}`
                                    : "Scan barcode"
                        }
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        onKeyDown={handleBarcode}
                      />
                    </div>

                    <div>
                      <label className="premium-label">Special Instruction</label>
                      <textarea
                        className="premium-input premium-textarea"
                        disabled={isDeliveryBlocked}
                        value={specialInstruction}
                        onChange={(e) => setSpecialInstruction(e.target.value)}
                        placeholder="Add handling notes, special reminders, or delivery instructions..."
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="table-shell">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Received Date</th>
                      <th>Barcode</th>
                      <th>Lot</th>
                      <th>Expiry</th>
                      <th>Description</th>
                      <th>Unit Measure</th>
                      <th>Qty</th>
                      <th>Reason</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(showHistoryItems ? dnItems : scanned).map((it, i) => {
                      const itemStatus = getItemStatusLabel(it);
                      const reasonText = getItemReason(it);

                      return (
                        <tr key={i}>
                          <td>
                            {itemStatus === "VOIDED" ? (
                              <span className="returned-badge">VOIDED</span>
                            ) : itemStatus === "RETURNED" ? (
                              <span className="returned-badge">RETURNED</span>
                            ) : itemStatus === "DELIVERED" ? (
                              <span className="delivered-badge">DELIVERED</span>
                            ) : (
                              <span className="released-badge">RELEASED</span>
                            )}
                          </td>

                          <td>{getItemReceivedDate(it)}</td>
                          <td>{it.Barcode}</td>
                          <td>{it.LotNumber}</td>
                          <td>{toDateOnlyStr(it.ExpiryDate)}</td>
                          <td>{it.Description}</td>
                          <td>{getUnitMeasure(it)}</td>
                          <td>{it.QtyDelivered || it.Qty || 1}</td>
                          <td>{reasonText}</td>
                        </tr>
                      );
                    })}

                    {(showHistoryItems ? dnItems : scanned).length === 0 && (
                      <tr>
                        <td colSpan={9} className="empty-state-cell">
                          No items to show.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dn-actions">
                {!isDeliveryBlocked && (
                  <>
                    <button className="cancel-btn" onClick={cancelDelivery}>
                      Cancel
                    </button>

                    <button className="confirm-btn" onClick={confirmDelivery}>
                      Confirm Release / Stock Out
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {showTimelineModal && selectedSO && (
        <div className="modal-overlay dn-timeline-modal-overlay">
          <div className="modal-content dn-timeline-modal">
            <div className="dn-timeline-modal-head">
              <div>
                <div className="dn-timeline-kicker">Monitoring</div>
                <h3>Sales Order Timeline</h3>
                <span>{selectedSO.SONumber || selectedSO.sonumber || "-"}</span>
              </div>

              <div className="dn-timeline-modal-actions">
                <button
                  type="button"
                  className="confirm-btn dn-timeline-refresh"
                  onClick={() => loadSOTimeline(selectedSO.SOID || selectedSO.soid)}
                  disabled={timelineLoading}
                >
                  {timelineLoading ? "Loading..." : "Refresh"}
                </button>

                <button
                  type="button"
                  className="dn-timeline-close-btn"
                  onClick={closeTimelineModal}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="dn-timeline-modal-body">
              {renderTimelineContent()}
            </div>
          </div>
        </div>
      )}

      {showPrintPreview && (
        <div className="modal-overlay">
          <div className="modal-content dn-preview-modal">
            <div ref={printRef}>
              <DnPrintTemplate
                dn={printDN}
                so={selectedSO}
                items={printItems}
                mode="ORIGINAL"
                copyType={copyType}
              />
            </div>

            <div className="modal-actions">
              <button className="confirm-btn" onClick={downloadDNCopiesPDF}>
                DOWNLOAD 4 COPIES PDF
              </button>

              <button className="cancel-btn" onClick={() => setShowPrintPreview(false)}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkDeliveredModal && canMarkDeliveredDN && (
        <div className="modal-overlay">
          <div className="modal-content mark-delivered-modal">
            <h3 style={{ marginTop: 0 }}>
              Mark DN as Delivered — {markDeliveredDN?.DNNumber}
            </h3>

            <p className="inline-note">
              Select the actual date the customer received the item(s).
            </p>

            <div style={{ marginTop: 12 }}>
              <label className="premium-label">Received Date</label>
              <input
                type="date"
                className="premium-input"
                value={markDeliveredDate}
                max={getTodayDateInput()}
                onChange={(e) => setMarkDeliveredDate(e.target.value)}
                disabled={markDeliveredSaving}
              />
            </div>

            <div className="modal-actions">
              <button
                className="confirm-btn"
                onClick={confirmMarkDelivered}
                disabled={markDeliveredSaving}
              >
                {markDeliveredSaving ? "SAVING..." : "CONFIRM DELIVERED"}
              </button>

              <button
                className="cancel-btn"
                disabled={markDeliveredSaving}
                onClick={() => {
                  setShowMarkDeliveredModal(false);
                  setMarkDeliveredDN(null);
                  setMarkDeliveredDate(getTodayDateInput());
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {showReturnModal && !isRestrictedDeliveryRole && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 style={{ marginTop: 0 }}>Return Items to Stock — {returnDN?.DNNumber}</h3>

            <div style={{ marginTop: 10 }}>
              <label className="premium-label">Return Reason</label>
              <textarea
                placeholder="e.g. Wrong item delivered / Damaged / Customer returned / Expired"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                disabled={returnSaving}
                className="premium-input premium-textarea"
              />
            </div>

            <div className="scan-row single-col" style={{ marginTop: 10 }}>
              <div>
                <label className="premium-label">Scan Barcode</label>
                <input
                  autoFocus
                  placeholder="Scan barcode then press Enter"
                  value={returnBarcode}
                  onChange={(e) => setReturnBarcode(e.target.value)}
                  onKeyDown={handleReturnBarcode}
                  disabled={returnSaving}
                  className="premium-input"
                />
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <h4 style={{ margin: "8px 0" }}>Scanned for Return</h4>

              <table className="premium-table modal-table">
                <thead>
                  <tr>
                    <th>Barcode</th>
                    <th>Lot</th>
                    <th>Expiry</th>
                    <th>Description</th>
                    <th>Unit Measure</th>
                    <th>Qty</th>
                  </tr>
                </thead>

                <tbody>
                  {returnScanned.map((it, i) => (
                    <tr key={i}>
                      <td>{it.Barcode}</td>
                      <td>{it.LotNumber}</td>
                      <td>{toDateOnlyStr(it.ExpiryDate)}</td>
                      <td>{it.Description}</td>
                      <td>{getUnitMeasure(it)}</td>
                      <td>{it.Qty}</td>
                    </tr>
                  ))}

                  {returnScanned.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty-state-cell">
                        No scanned items yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button className="confirm-btn" onClick={confirmReturnToStock} disabled={returnSaving}>
                {returnSaving ? "RETURNING..." : "CONFIRM RETURN"}
              </button>

              <button
                className="cancel-btn"
                disabled={returnSaving}
                onClick={() => {
                  setShowReturnModal(false);
                  setReturnDN(null);
                  setReturnDnItems([]);
                  setReturnScanned([]);
                  setReturnBarcode("");
                  setReturnReason("");
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && !isRestrictedDeliveryRole && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 style={{ marginTop: 0 }}>
              Cancel DN (Scan Required) — {cancelDNHeader?.DNNumber}
            </h3>

            <div style={{ marginTop: 10 }}>
              <label className="premium-label">Cancel Reason</label>
              <textarea
                placeholder="e.g. Delivery aborted / Customer refused / Wrong items"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                disabled={cancelSaving}
                className="premium-input premium-textarea"
              />
            </div>

            <div className="scan-row single-col" style={{ marginTop: 10 }}>
              <div>
                <label className="premium-label">Scan Barcode</label>
                <input
                  autoFocus
                  placeholder="Scan barcode then press Enter"
                  value={cancelBarcode}
                  onChange={(e) => setCancelBarcode(e.target.value)}
                  onKeyDown={handleCancelBarcode}
                  disabled={cancelSaving}
                  className="premium-input"
                />
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <h4 style={{ margin: "8px 0" }}>Scanned for Cancel</h4>

              <table className="premium-table modal-table">
                <thead>
                  <tr>
                    <th>Barcode</th>
                    <th>Lot</th>
                    <th>Expiry</th>
                    <th>Description</th>
                    <th>Unit Measure</th>
                    <th>Qty</th>
                  </tr>
                </thead>

                <tbody>
                  {cancelScanned.map((it, i) => (
                    <tr key={i}>
                      <td>{it.Barcode}</td>
                      <td>{it.LotNumber}</td>
                      <td>{toDateOnlyStr(it.ExpiryDate)}</td>
                      <td>{it.Description}</td>
                      <td>{getUnitMeasure(it)}</td>
                      <td>{it.Qty}</td>
                    </tr>
                  ))}

                  {cancelScanned.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty-state-cell">
                        No scanned items yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button className="confirm-btn" onClick={confirmCancelDN} disabled={cancelSaving}>
                {cancelSaving ? "CANCELLING..." : "CONFIRM CANCEL"}
              </button>

              <button
                className="cancel-btn"
                disabled={cancelSaving}
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelDNHeader(null);
                  setCancelDnItems([]);
                  setCancelBarcode("");
                  setCancelScanned([]);
                  setCancelReason("");
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
